<template lang="pug">
	dx-validation-group.user-registration.flex-col.space-large
		.login-header
			img(:src="require('assets/logo_transparent.png')" height="60px")
			div {{$tCaption('New account registration form')}}
		template(v-if="!emailValidation.visible")
			.flex-row.space-large.wrap
				.flex-col.space.flex-basis-200.flex-1.tb-padding
					.font-big.upper.grey {{$tCaption('Account info')}}
					.flex-col.space-large
						.flex-col.field.space
							.font-small.upper.grey {{$tCaption('Email')}}
							dx-text-box(:value.sync="userInfo.Login" :disabled="isLoading || !!inviteData" width="100%")
								dx-validator
									dx-required-rule(:message="$tCaption('Email is required')")
						.flex-col.space(v-if="isPersonalEmail")
							.flex-col.space.field
								.font-small.upper.grey {{$tCaption('Use case')}}
								DxTextArea(:value.sync="registrationUseCase" :disabled="isLoading || !!inviteData" width="100%")
									dx-validator
										dx-required-rule(:message="$tCaption('Use case is required')")
							.font-tiny.grey {{$tCaption('Due to the sensitive nature of AI Usage, the Adthos platform currently only allows users from Publishers, Broadcasters, Agencies and Advertisers. We do not allow private email addresses. If you are a member of an organization, please provide the use case below for the intended use of our platform. Our staff will then review your application.')}}
						.flex-col.field.space
							.font-small.upper.grey {{$tCaption('Password')}}
							dx-text-box(
								:value.sync="userInfo.Password"
								:disabled="isLoading"
								width="100%"
								mode="password"
								:useMaskedValue="true"
								:input-attr="{ autocomplete: 'new-password', id: 'new-password' }"
							)
								dx-validator
									dx-required-rule(:message="$tCaption('Password is required')")
						.flex-col.field.space
							.font-small.upper.grey {{$tCaption('Enter password again')}}
							dx-text-box(
								:value.sync="userInfo.PasswordAgain"
								:disabled="isLoading"
								width="100%"
								mode="password"
								:useMaskedValue="true"
								:input-attr="{ autocomplete: 'new-password', id: 'new-password-confirm' }"
							)
								dx-validator
									dx-required-rule(:message="$tCaption('Enter password again')")
									dx-custom-rule(:validation-callback="() => !!(userInfo.PasswordAgain === userInfo.Password)", :message="$tCaption('Entered values are different')")
						.flex-col.field.space
							.font-small.upper.grey {{$tCaption('Name')}}
							dx-text-box(
								:value.sync="userInfo.Contact.Title"
								:disabled="isLoading"
								width="100%"
							)
								dx-validator
									dx-required-rule(:message="$tCaption('Name is required')")
						.flex-col.field.space
							.font-small.upper.grey {{$tCaption('Country')}}
							Dropdown(ref="countryDD", 
								:getTotal="(data) => data.TotalNumberOfRecords", :getItems="(data) => data.Results", :getData="$Data.getExternalCountries",
								:columns="[{ dataField: 'Name', caption: $tCaption('Country') }]",
								displayExpr="Name",
								valueExpr="Oid",
								:selectedItems="userInfo.Company.Country.Oid",
								:showColumnHeaders="false",
								:filtersVisible="false",
								:searchVisible="true",
								:multiple="false",
								:disabled="isLoading",
								@select-data="onCountryChange")
								dx-validator
									dx-required-rule(:message="$tCaption('Field is required')")
						.flex-col.field.space
							.font-small.upper.grey {{$tCaption('Language')}}
							DxSelectBox(
								:value="userInfo.Contact.LanguageCode",
								:disabled="isLoading",
								width="100%",
								:dataSource="locales",
								displayExpr="displayName",
								valueExpr="locale",
								:searchEnabled="true",
								@valueChanged="v => userInfo.Contact.LanguageCode = v.value"
							)
						.flex-col.field.space
							.font-small.upper.grey {{$tCaption('Phone')}}
							VuePhoneInput(ref="phoneNumber", :value="userInfo.Contact.Phone", :disabled="isLoading || !userInfo.Company.Country.Oid", 
								@country-change="onPhoneCountryChanged", @change="v => userInfo.Contact.Phone = v",
								@default-country-response="onDefaultCountryResponse")
								dx-validator
									dx-custom-rule(:validation-callback="() => !!(userInfo.Contact.Phone)", :message="$tCaption('Please, enter phone number')")
						.flex-row.space
							DxCheckBox(:value.sync="agreeTerms")
								dx-validator
									dx-required-rule
							span {{$tCaption('I agree to the')}}
							a(@click="openLink('terms')") {{$tCaption('terms and conditions')}}
						.flex-row.space
							DxCheckBox(:value.sync="agreePolicy")
								dx-validator
									dx-required-rule
							span {{$tCaption('I agree to the')}}
							a(@click="openLink('privacy')") {{$tCaption('privacy policy')}}
				.flex-col.space.flex-basis-200.flex-1.tb-padding.organization-block()
					template(v-if="userInfo.Company.CompanyType === null && !inviteData && !companyInviteData")
						.font-small.upper.grey {{$tCaption('Organization type')}}
						ProductSelectionComponent(:items="$companyTypes.map(f => ({ id: f.id, name: f.name() }))", @select="type => userInfo.Company.CompanyType = type.id")
							template(v-slot:card="{ data }")
								.flex-row.space.align-center
									i(:class="[data.icon]")
									span {{data.name}}
					template(v-else)
						.flex-row.space.align-center
							.font-big.upper.grey(v-if="!createCompany || (canSelectOrganization && wantToCreateNewOrganization === false)") {{orgTypeName}}
							.font-big.upper.grey(v-else) {{$tCaption('New')}} {{orgTypeName.toLowerCase()}}
							a(v-if="!inviteData && !companyInviteData", @click="userInfo.Company.CompanyType = null; userInfo.Company.Oid = null; createCompany= true")
								i.fas.fa-edit
						.flex-col.space-large
							template(v-if="canSelectOrganization")
								.flex-col.space-large
									.font-small(v-if="userInfo.Company.CompanyType === 1") {{$tCaption('The Adthos Platform comes with a pre-loaded FCC database of stations and groups. If you are a part of a licensed FCC organization, you can claim your organization below.')}}
									.font-small(v-if="userInfo.Company.CompanyType === 2") {{$tCaption('The Adthos Platform comes with a pre-loaded EDI Agency list. If you are a part of an existing EDI agency, you can claim your organization below.')}}
									.flex-col.space
										.flex-row.space
											DxCheckBox(:value="wantToCreateNewOrganization === false", @value-changed="(v) => wantToCreateNewOrganization = v.value === false")
											span {{$tCaption('My organization is a licensed FCC organization')}}
										.flex-row.space
											DxCheckBox(:value="wantToCreateNewOrganization === true", @value-changed="(v) => wantToCreateNewOrganization = v.value === true")
											span {{$tCaption('I want to register a new Organization')}}
							template(v-if="(!canSelectOrganization && !createCompany) || (canSelectOrganization && wantToCreateNewOrganization === false)")
								.flex-col.space-large
									Dropdown(ref="organizationDD",
										:getTotal="(data) => data.TotalNumberOfRecords", :getItems="(data) => data.Results", :getData="(a,b,c) => $Data.getExternalCompanies(a,b, {...(c || {}), byType: userInfo.Company.CompanyType, forceTokenAuth: false, onlyUnregistered: true }, userInfo.Company.Country.Oid)",
										:columns="[{ dataField: 'Name', caption: $tCaption('Company') }]",
										displayExpr="Name",
										valueExpr="Oid",
										:placeholder="$tCaption('Find') + ' ' + orgTypeName.toLowerCase()",
										:selectedItems="userInfo.Company.Oid",
										:showColumnHeaders="false",
										:filtersVisible="false",
										:searchVisible="true",
										:multiple="false",
										:minSearchLength="3",
										:disabled="isLoading || !userInfo.Company.Country.Oid || !!inviteData",
										:noDataText="$tCaption('Type at least 3 characters')",
										@select-data="item => userInfo.Company.Oid = item ? item.Oid : null",
										@search="companySearched = true; companyNotFound = false",
										@data-loaded="(data) => companyNotFound = !data || !data.length")
										.flex-row.space.align-center(v-if="!inviteData && false", slot="bottom")
											span {{$tCaption('Select or')}}
											a(@click="createOrganization") {{$tCaption('create new')}} {{orgTypeName.toLowerCase()}}
										dx-validator
											dx-custom-rule(:validation-callback="() => !!userInfo.Company.Oid", :message="$tCaption('You must select or create new organization')")
							.flex-col.space(v-if="(!canSelectOrganization && createCompany) || (canSelectOrganization && wantToCreateNewOrganization === true)")
								.flex-col.space-large
									.flex-col.field.space
										.font-small.upper.grey {{$tCaption('Name')}}
										DxTextBox(:value.sync="userInfo.Company.Name", :disabled="isLoading")
											dx-validator
												dx-required-rule(:message="$tCaption('Field is required')")
									.flex-col.field.space
										.font-small.upper.grey {{$tCaption('Address')}}
										DxTextBox(:value.sync="userInfo.Company.Address", :disabled="isLoading")
											dx-validator
												dx-required-rule(:message="$tCaption('Field is required')")
									.flex-col.field.space
										.font-small.upper.grey {{$tCaption('Address 2')}}
										DxTextBox(:value.sync="userInfo.Company.Address2", :disabled="isLoading")
									.flex-col.field.space
										.font-small.upper.grey {{$tCaption('Address 3')}}
										DxTextBox(:value.sync="userInfo.Company.Address3", :disabled="isLoading")
									.flex-col.field.space
										.font-small.upper.grey {{$tCaption('City')}}
										DxTextBox(:value.sync="userInfo.Company.City", :disabled="isLoading")
											dx-validator
												dx-required-rule(:message="$tCaption('Field is required')")
									.flex-col.field.space
										.font-small.upper.grey {{$tCaption('State')}}
										DxTextBox(:value.sync="userInfo.Company.State", :disabled="isLoading")
									.flex-col.field.space
										.font-small.upper.grey {{$tCaption('Zip code')}}
										DxTextBox(:value.sync="userInfo.Company.Zip", :disabled="isLoading")
			.flex-row.space.justify.boittom-actions
				.flex-row.space
					slot(name="bottom")
				.dx-field
					dx-button(
						@click="onRegisterClick"
						:icon="isRegistering ? 'fas fa-circle-notch fa-spin' : ''"
						:disabled="isRegistering"
						type="success"
						:text="isRegistering ? $tCaption('Registering') : $tCaption('Register')"
						width="100%"
					)
		template(v-else)
			.flex-row.space-large.wrap
				.flex-col.space.flex-basis-200.flex-1.tb-padding
					.font-big.upper.grey {{$tCaption('Confirmation code')}}
					Loader(v-if="!emailValidation.hash", :text="$tCaption('Generating code')")
					template(v-else)
						.flex-row
							dx-text-box(
								:value.sync="emailValidation.code"
								:disabled="isLoading"
								width="100%"
							)
								dx-validator
									dx-required-rule(:message="$tCaption('Code is required')")
							.dx-field
								dx-button(
									@click="onValidateEmailClick"
									:icon="isLoading ? 'fas fa-circle-notch fa-spin' : ''"
									:disabled="isLoading || !emailValidation.hash"
									type="success"
									:text="isLoading ? $tCaption('Validating') : $tCaption('Confirm')"
								)
						template(v-if="!hasValidationData")
							br
							span {{$tCaption('We\'ve sent a confirmation code to your email address. Please check your email and spam box for the email, and paste the code in the field above.')}}
							a(v-if="!emailValidation.resendTimer", @click="v => sendEmailValidation(true)") {{$tCaption('Send again')}}
							.grey(v-else) {{$tCaption('Resend email in')}} {{emailValidation.resendTimer}} {{$tCaption('seconds')}}..
			.flex-row.space.justify.boittom-actions
				.flex-row.space
					dx-button(v-if="!hasValidationData", @click="onValidationBack", :text="$tCaption('Back')")
</template>

<script>
	import _ from 'lodash';
	import DxButton from 'devextreme-vue/button';
	import DxSelectBox from 'devextreme-vue/select-box';
	import DxTextBox from 'devextreme-vue/text-box';
	import DxTextArea from 'devextreme-vue/text-area';
	import DxDateBox from 'devextreme-vue/date-box';
	import DxValidationGroup from 'devextreme-vue/validation-group';
	import DxValidator, { DxRequiredRule, DxCustomRule } from 'devextreme-vue/validator';
	import Loader from 'app-core/Loader.vue';
	import Dropdown from 'app-core/Dropdown.vue';
	import ProductSelectionComponent from 'view/proposal/ProductSelectionComponent.vue';
	import VuePhoneInput from 'vue-phone-input';
	import { DxCheckBox } from 'devextreme-vue/check-box';

	export default {
		translationCategory: 'user.Registration',
		name: 'user-registration',
		components: {
			DxButton, DxTextBox, Loader, DxDateBox, Dropdown, DxSelectBox,
			DxValidationGroup, DxValidator, DxRequiredRule, DxCustomRule, ProductSelectionComponent, DxCheckBox,
			VuePhoneInput, DxTextArea
		},
		props: ['inviteData', 'emailValidationData', 'companyInviteData', 'companyInvite'],
		data() {
			this.$companyTypes = [{ id: 0, name: () => this.$tCaption('Advertiser'), icon: 'fas fa-user' }, { id: 1, name: () => this.$tCaption('Station owner'), icon: 'fas fa-broadcast-tower' }, { id: 2, name: () => this.$tCaption('Agency'), icon: 'fas fa-briefcase' }];
			return {
				isLoading: false,
				isRegistering: false,
				userInfo: {
					Oid: null,
					Login: null,
					Password: null,
					PasswordAgain: null,
					Contact: {
						Title: null,
						Email: null,
						Phone: null,
						RegistrationInfo: null,
						LanguageCode: window?.navigator?.language
					},
					Company: {
						Oid: this.inviteData && this.inviteData.Company.Oid,
						Name: this.companyInviteData && this.companyInviteData.Name || null,
						Country: {
							Oid: null,
							Code: null
						},
						Address: null,
						Address2: null,
						Address3: null,
						City: null,
						State: null,
						Zip: null,
						CompanyType: this.companyInviteData ? this.companyInviteData.CompanyType : null
					},
					CompanyInvitationToken: this.companyInvite
				},
				emailValidation: {
					visible: !!this.emailValidationData,
					sent: !!this.emailValidationData,
					resendTimer: 0,
					hash: this.emailValidationData && this.emailValidationData.hash || null,
					code: this.emailValidationData && this.emailValidationData.code || null
				},
				companySearched: false,
				companyNotFound: false,
				createCompany: !(this.inviteData && this.inviteData.Company.Oid),
				agreeTerms: false,
				agreePolicy: false,
				registered: false,
				browserLocationInfo: {},
				wantToCreateNewOrganization: null,
				countryCode: null,
				registrationUseCase: null,
				isPersonalEmail: false
			};
		},
		computed: {
			orgTypeName() {
				const companyType = this.userInfo.Company.CompanyType;
				return companyType !== null ? this.$companyTypes.filter(f => f.id === companyType)[0].name() : this.$tCaption('Organization');
			},
			hasValidationData() {
				return this.emailValidationData && this.emailValidationData.hash && this.emailValidationData.code;
			},
			canSelectOrganization() {
				return this.userInfo.Company && this.userInfo.Company.Country && this.userInfo.Company.Country.Code === 'US' && [1,2].indexOf(this.userInfo.Company.CompanyType) > -1;
			},
			locales() {
				return this.$Data.langmapItems();
			}
		},
		watch: {
			'emailValidation.resendTimer'() {
				if (this.$resendTimer)
					clearInterval(this.$resendTimer);
				setTimeout(() => {
					if (this._isDestroyed || (this.emailValidation.resendTimer || 0) <= 0) return;
					this.emailValidation.resendTimer--;
				}, 1000);
			},
			'userInfo.Company.Country.Code'() {
				this.userInfo.Company.Oid = null;
			}
		},
		mounted() {
			if (__DEBUG__)
				window.registration = this;
			if (this.inviteData) {
				this.userInfo.Login = this.inviteData.Email;
				this.userInfo.Contact.Oid = this.inviteData.Oid;
				this.userInfo.Contact.Phone = this.inviteData.Phone;
				this.userInfo.Contact.Email = this.inviteData.Email;
				this.userInfo.Contact.Title = this.inviteData.Title;
			}
			if (this.companyInviteData) {
				this.userInfo.Login = this.companyInviteData.Email;
			}
		},
		destroyed() {
			delete this.$companyTypes;
		},
		methods: {
			async checkIsPersonal() {
				if (!this.userInfo.Login) return;
				
				this.isPersonalEmail = `${(await this.$Data.getExternalApi('Security/IsPersonalEmail', `email=${this.userInfo.Login}`))}` == 'true';
			},
			onDefaultCountryResponse(code) {
				if (__DEBUG__)
					console.debug('On default country response', { code });
				this.$Data.callMethod('getUserIP').then(ip => {
					this.userInfo.Contact.RegistrationInfo = `${ip}, ${code}, ${navigator && navigator.userAgent}`.substr(0,240);
					this.$Data.getBrowserLocationByIp(ip).then(r => {
						if (this._isDestroyed || !r) return;
						this.browserLocationInfo.City = r.city;
						this.browserLocationInfo.State = r.region;
						this.browserLocationInfo.Zip = r.postal_code;
						_.assign(this.userInfo.Company, this.browserLocationInfo);
					});
				});
			},
			onValidationBack() {
				if (this.emailValidationData && this.emailValidationData.code) {
					this.$emit('registered');
				} else {
					this.emailValidation.visible = false;
				}
			},
			openLink(routeName) {
				let routeData = this.$router.resolve({ name: routeName });
				window.open(routeData.href, '_blank');
			},
			onCountryChange(item) {
				if (!this.inviteData?.Company?.Oid) {
					this.userInfo.Company.Country.Oid = item ? item.Oid : null;
					this.userInfo.Company.Country.Code = item ? item.Code : null;
				}
				if (item) {
					this.$refs.phoneNumber.setCountryCode(item.Code);
				}
			},
			onPhoneCountryChanged(country) {
				if (__DEBUG__)
					console.debug('On phone country changed', { country });
				this.$Data.getExternalApi('Service/GetCountries', `searchByCode=${country}`).then(r => {
					if (__DEBUG__)
						console.debug('Country loaded', r);
					if (!this.userInfo.Company.Country.Oid && !this.inviteData?.Company?.Oid) {
						this.userInfo.Company.Country.Oid = r && r.Results && r.Results[0] && r.Results[0].Oid;
					}
				});
			},
			createOrganization() {
				this.createCompany = true;
				this.userInfo.Company.Name = this.$refs.organizationDD.searchText;
				this.userInfo.Company = {
					Oid: null,
					Name: this.$refs.organizationDD.searchText,
					Country: {
						Oid: this.userInfo.Company.Country.Oid
					},
					Address: null,
					Address2: null,
					Address3: null,
					City: null,
					State: null,
					Zip: null,
					CompanyType: this.userInfo.Company.CompanyType,
					...this.browserLocationInfo
				};
			},
			async onRegisterClick(e) {
				if (__DEBUG__)
					console.debug('On register click', e);

				await this.checkIsPersonal();
				if (!e.validationGroup.validate().isValid) {
					return;
				}
				if (!this.inviteData && this.userInfo.Company.CompanyType === null && !this.userInfo.Company.Oid) {
					this.$notifyError(this.$tCaption('Please select organization type'));
					return;
				}

				this.register().then(() => {
					if (this.registered) {
						this.emailValidation.visible = true;
						this.emailValidation.code = null;
						if (!this.emailValidation.resendTimer)
							this.sendEmailValidation();
					}
				});	
			},
			sendEmailValidation(again) {
				if (again)
					this.emailValidation.resendTimer = 120;
				this.$Data.getExternalApi('Security/ConfirmEmail', `email=${this.userInfo.Contact.Email || this.userInfo.Login}`).then(r => {
					if (__DEBUG__)
						console.debug('ConfirmEmail response', { r });
					if (r.Success) {
						this.emailValidation.hash = r.Data;
						if (!this.emailValidation.resendTimer)
							this.emailValidation.resendTimer = 120;
					} else {
						this.$notifyError(r.ErrorMessage);
					}
				});
			},
			onValidateEmailClick(e) {
				if (!e.validationGroup.validate().isValid) {
					return;
				}
				this.isLoading = true;
				this.$Data.getExternalApi('Security/ValidateEmailCode', `code=${this.emailValidation.code}&hash=${encodeURIComponent(this.emailValidation.hash)}`).then(r => {
					if (__DEBUG__)
						console.debug('ValidateEmailCode response', { r });
					if (r.Success && r.Data === true) {
						this.isLoading = false;
						this.$emit('registered', r.Data);
					} else {
						this.$notifyError(r.ErrorMessage);
					}
				}).finally(() => this.isLoading = false);
			},
			register() {
				if (this.registered) return Promise.resolve(true);
				if (this.isLoading) return;
				if (__DEBUG__)
					console.debug('Register');
				
				this.isRegistering = true;
				this.isLoading = true;
				return this.$Data.postExternalApi('Security/CreateUserProfile', { 
					...this.userInfo, 
					Contact: { 
						...this.userInfo.Contact,
						Email: this.userInfo.Login
					},
					RegistrationUseCase: this.registrationUseCase
				}).then(r => {
					if (this._isDestroyed) return;
					if (r.Success) {
						this.registered = true;
						if (!this.createCompany) {
							this.$notifySuccess(this.$tCaption('You have been registered. But you need wait until your account will be approved by organization manager'), 10000);
						}
					} else {
						this.$notifyError(r.ErrorMessage);
					}
					return r;
				}).finally(() => {
					this.isRegistering = false;
					this.isLoading = false;	
				});
			}
		}
	};
</script>
<style lang='scss'>
	.user-registration {
		width: 90vw;
		max-width: 600px;

		.boittom-actions {
			margin-top: 40px;
		}

		.organization-block {
			padding-left: 20px;
			border-left: 1px solid #f2f2f2;
		}

		.grey {
			color: #c1c1c1 !important;
		}

		.product {
			font-size: 16px !important;
			min-width: 100% !important;
			min-height: auto !important;
		}

		.country-list {
			max-height: 150px !important;
		}
	}
</style>
 